import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
    Badge,
    Button,
    Card,
    Col,
    Container,
    Form,
    Row,
    Spinner,
    Table,
    Modal, Alert,
} from "react-bootstrap";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";

import { t } from "i18next";

import DataManager from "../../../General/DataManager";
import UserClass from "../../../General/LocalStorage/UserClass";
import Functions from "../../../General/Functions";
import { exportTicketsCsv, exportTicketsPdf } from "./ExportService";
import TablePagination from "../../../Layouts/TablePagination/TablePagination";
import DualScrollTable from "../../../Layouts/DualScrollTable/DualScrollTable";
import styles from "./BoardingTickets.module.scss";

// Per-column filters (filter row below the table header)
const EMPTY_COLUMN_FILTERS = {
    holderName: "",
    bookingReference: "",
    number: "",
    destinationPort: "",
    type: "",
    tariff: "",
    total: "",
    boarded: "",
    status: "",
    channel: "",
};

const CATEGORY_KEYS = ["passengers", "infants", "accessories", "vehicles"];

const TYPE_FILTER_OPTIONS = [
    { value: "passengers", label: "PAX" },
    { value: "infants", label: "INF" },
    { value: "accessories", label: "ACC" },
    { value: "vehicles", label: "VEI" },
];

// Case and accent insensitive comparison
const normalizeText = (value) =>
    String(value ?? "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim();

const BoardingTickets = () => {
    const navigate = useNavigate();
    const { routeId } = useParams();
    const [searchParams] = useSearchParams();
    const description = searchParams.get("description");
    const departureTime = searchParams.get("departureTime");
    const port = searchParams.get("port");

    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState(t("all"));
    const [channelFilter, setChannelFilter] = useState(t("all"));
    const [confirmation, setConfirmation] = useState({
        show: false,
        ticket: null,
        action: null,
    });
    const [selectedTickets, setSelectedTickets] = useState([]);
    const [bulkConfirmation, setBulkConfirmation] = useState({
        show: false,
        action: null,
    });

    //const limit = 10;
    const [limit, setLimit] = useState(10);

    const [page, setPage] = useState(1);

    const [columnFilters, setColumnFilters] = useState(EMPTY_COLUMN_FILTERS);

    const hasColumnFilters = Object.values(columnFilters).some(value => value.trim() !== "");

    const updateColumnFilter = (key, value) => {
        setColumnFilters(prev => ({ ...prev, [key]: value }));
        // Avoid bulk actions on selected tickets hidden by the new filter
        setSelectedTickets([]);
    };

    const clearColumnFilters = () => {
        setColumnFilters(EMPTY_COLUMN_FILTERS);
        setSelectedTickets([]);
    };

    useEffect(() => {
        setPage(1);
    }, [search, statusFilter, channelFilter, limit, columnFilters]);

    const openConfirmation = (ticket, status) => {
        setConfirmation({
            show: true,
            ticket,
            action: status === t("boarded") ? "checkout" : "checkin",
        });
    };

    const confirmAction = async () => {
        if (!confirmation.ticket) return;

        try {

            const token = UserClass.getInstance().getTokenUser();

            const operation = confirmation.action === "checkin" ? "checkin" : "checkout";

            const { msgError } = await DataManager.changeTicketBoardingStatus(token, operation, routeId, confirmation.ticket.departurePort, [confirmation?.ticket?.number]);

            if (msgError) {
                Functions.alertMsg(msgError);
                return;
            }

            const message =
                operation === "checkin"
                    ? t("checkin_completed_successfully")
                    : t("checkout_completed_successfully");
            Functions.alertMsgSuccess(
                message
            );

            setConfirmation({
                show: false,
                ticket: null,
                action: null,
            });

            // Refresh the list
            getTickets(search.length >= 3 ? search : "");

        } catch (error) {
            Functions.alertMsg(t("msgErrorGeneral"));
        }


        setConfirmation({
            show: false,
            ticket: null,
            action: null,
        });
    };

    const openBulkConfirmation = (action) => {

        if (selectedTickets.length === 0) {
            Functions.alertMsg(t("no_tickets_selected"));
            return;
        }

        setBulkConfirmation({
            show: true,
            action,
        });
    };

    const confirmBulkAction = async () => {

        if (selectedTickets.length === 0) return;

        try {

            const token = UserClass.getInstance().getTokenUser();

            const numbers = selectedTickets.map(
                ticket => ticket.number
            );

            const operation = bulkConfirmation.action;

            const departurePort =
                selectedTickets[0].departurePort;


            const { msgError } =
                await DataManager.changeTicketBoardingStatus(
                    token,
                    operation,
                    routeId,
                    departurePort,
                    numbers
                );


            if (msgError) {
                Functions.alertMsg(msgError);
                return;
            }


            Functions.alertMsgSuccess(
                operation === "checkin"
                    ? t("checkin_completed_successfully")
                    : t("checkout_completed_successfully")
            );


            setBulkConfirmation({
                show: false,
                action: null,
            });


            setSelectedTickets([]);


            // reload data from backend
            getTickets(search.length >= 3 ? search : "");


        } catch (error) {

            Functions.alertMsg(
                t("msgErrorGeneral")
            );

        }
    };

    const getTickets = useCallback(async (search) => {

        try {

            setLoading(true);

            const token = UserClass.getInstance().getTokenUser();

            const { data, msgError } =
                await DataManager.getRouteTickets(token, routeId, port, search || "");

            if (msgError) {
                Functions.alertMsg(msgError);
                return;
            }

            setTickets(data || []);

        } catch {
            Functions.alertMsg(t("msgErrorGeneral"));
        } finally {
            setLoading(false);
        }
    }, [routeId, port]);

    useEffect(() => {
        if (!routeId) return;
        const timeout = setTimeout(() => {
            getTickets(search.length >= 3 ? search : "");
        }, 500);

        return () => clearTimeout(timeout);
    }, [search, routeId, getTickets]);

    const getTicketStatus = (ticket) => {
        if (ticket.stato === "NoShow") return "No Show";
        if (ticket.stato === "boarded" || ticket.stato === "Imbarcato") return t('boarded');
        return "Pending";
    };

    const getBadgeVariant = (status) => {
        switch (status) {
            case t('boarded'):
                return "success";
            case "Checked In":
                return "primary";
            case "Pending":
                return "warning";
            case "No Show":
                return "danger";
            default:
                return "secondary";
        }
    };

    const matchesColumnFilters = (ticket, status) => {
        const f = columnFilters;

        const textMatch = (value, filter) =>
            !filter.trim() || normalizeText(value).includes(normalizeText(filter));

        // Total / Boarded columns show one number per category: match any of them
        const numberMatch = (field, filter) =>
            !filter.trim() ||
            CATEGORY_KEYS.some(key =>
                ticket?.[key] && String(ticket[key]?.[field] ?? "") === filter.trim()
            );

        return (
            textMatch(ticket?.holderName, f.holderName) &&
            textMatch(ticket?.bookingReference, f.bookingReference) &&
            textMatch(ticket?.number, f.number) &&
            textMatch(ticket?.destinationPort, f.destinationPort) &&
            (!f.type || Boolean(ticket?.[f.type])) &&
            textMatch(ticket?.tariff, f.tariff) &&
            numberMatch("total", f.total) &&
            numberMatch("boarded", f.boarded) &&
            (!f.status || status === f.status) &&
            (!f.channel || ticket?.issueType === f.channel)
        );
    };

    const channelFilterOptions = useMemo(
        () => [...new Set(tickets.map(ticket => ticket?.issueType).filter(Boolean))],
        [tickets]
    );

    const filteredTickets = useMemo(() => {
        return tickets.filter(ticket => {
            const status = getTicketStatus(ticket);

            const matchesStatus =
                statusFilter === t("all") ||
                status === statusFilter;

            const matchesChannel =
                channelFilter === t("all") ||
                ticket.issueType === channelFilter;

            return matchesStatus && matchesChannel && matchesColumnFilters(ticket, status);

        });
    }, [tickets, statusFilter, channelFilter, columnFilters]);

    // Header totals follow every active filter (status, channel and column filters)
    const statsTickets = filteredTickets;

    const stats = useMemo(() => ({

        totalTickets: statsTickets.length,
        passengers: statsTickets.reduce(
            (sum, t) => sum + (t.passengers?.total ?? 0), 0
        ),
        boardedPassengers: statsTickets.reduce(
            (sum, t) => sum + (t.passengers?.boarded ?? 0),
            0
        ),
        infants: statsTickets.reduce(
            (sum, t) => sum + (t.infants?.total ?? 0), 0
        ),
        boardedInfants: statsTickets.reduce(
            (sum, t) => sum + (t.infants?.boarded ?? 0), 0
        ),
        accessories: statsTickets.reduce(
            (sum, t) => sum + (t.accessories?.total ?? 0),
            0
        ),
        boardedAccessories: statsTickets.reduce(
            (sum, t) => sum + (t.accessories?.boarded ?? 0),
            0
        ),
        vehicles: statsTickets.reduce(
            (sum, t) => sum + (t.vehicles?.total ?? 0),
            0
        ),
        boardedVehicles: statsTickets.reduce(
            (sum, t) => sum + (t.vehicles?.boarded ?? 0),
            0
        ),
        totalGrand:
            statsTickets.reduce(
                (sum, t) =>
                    sum +
                    (t.passengers?.total ?? 0) +
                    (t.infants?.total ?? 0) +
                    (t.accessories?.total ?? 0) +
                    (t.vehicles?.total ?? 0),
                0
            ),

        boardedGrand:
            statsTickets.reduce(
                (sum, t) =>
                    sum +
                    (t.passengers?.boarded ?? 0) +
                    (t.infants?.boarded ?? 0) +
                    (t.accessories?.boarded ?? 0) +
                    (t.vehicles?.boarded ?? 0),
                0
            ),

        noShowGrand:
            statsTickets.reduce(
                (sum, t) =>
                    sum +
                    ((t.passengers?.total ?? 0) - (t.passengers?.boarded ?? 0)) +
                    ((t.infants?.total ?? 0) - (t.infants?.boarded ?? 0)) +
                    ((t.accessories?.total ?? 0) - (t.accessories?.boarded ?? 0)) +
                    ((t.vehicles?.total ?? 0) - (t.vehicles?.boarded ?? 0)),
                0
            ),
        noShow: statsTickets.filter(t => t.stato === "NoShow").length,
    }), [statsTickets]);
    const totalResults = filteredTickets.length;

    const totalPages = Math.max(
        1,
        Math.ceil(totalResults / limit)
    );

    const paginatedTickets = useMemo(() => {
        const start = (page - 1) * limit;

        return filteredTickets.slice(start, start + limit);
    }, [filteredTickets, page, limit]);

    // Keep current page valid when the list shrinks (e.g. after a reload)
    useEffect(() => {
        if (page > totalPages) setPage(totalPages);
    }, [page, totalPages]);






    const StatCard = ({ title, color, grandTotal, pax, inf, acc, vei }) => (
        <Col md={4}>
            <Card className="shadow-sm border-0 h-100">
                <Card.Body>
                    <div className={`text-${color} small fw-semibold text-center`}>
                        {title}
                    </div>

                    {/*<div className="text-center mt-3 mb-3">
                        <h4 className="fw-bold mb-0">{grandTotal}</h4>
                    </div>*/}

                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <Badge bg="secondary">PAX</Badge>
                        <span className="fw-bold fs-5">{pax}</span>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <Badge bg="danger">INF</Badge>
                        <span className="fw-bold fs-5">{inf}</span>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-2">
                        <Badge bg="info">ACC</Badge>
                        <span className="fw-bold fs-5">{acc}</span>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-2">
                        <Badge bg="warning" text="dark">VEI</Badge>
                        <span className="fw-bold fs-5">{vei}</span>
                    </div>
                </Card.Body>
            </Card>
        </Col>
    );


    const exportRows = filteredTickets.map(ticket => ({
        [t('owner')]: ticket?.holderName?.toString() ?? '-',
        [t("booking")]: ticket?.bookingReference?.toString() ?? '-',
        [t("ticket")]: ticket?.number?.toString() ?? '-',
        [t('departure_time')]: ticket?.departureTime
            ? new Date(ticket.departureTime).toLocaleString()
            : "-",
        [t('departure_port')]: ticket?.departurePort,
        [t('arrival_port')]: ticket?.destinationPort,
        [t('status')]: getTicketStatus(ticket),
        [t('channel')]: ticket?.issueType ? (ticket?.issueType === "ticketOffice" ? t("ticket_office") : ticket?.issueType) : "-",
        [t('totalPassengers')]: ticket.passengers?.total ?? 0,
        [t('tariff')]: ticket?.tariff ?? '-',
        [t('boardedPassengers')]: ticket.passengers?.boarded ?? 0,
        [t('totalInfants')]: ticket.infants?.total ?? 0,
        [t('boardedInfants')]: ticket.infants?.boarded ?? 0,
        [t('totalAccessories')]: ticket.accessories?.total ?? 0,
        [t('totalBoardedAccessories')]: ticket.accessories?.boarded ?? 0,
        [t('totalVehicles')]: ticket.vehicles?.total ?? 0,
        [t('totalBoardedVehicles')]: ticket.vehicles?.boarded ?? 0,
    }));

    const channelLabel = (value) => {
        if (value === "ticketOffice") return t("ticket_office");
        if (value === "online") return t("online");
        return value;
    };

    // Filters narrowing the exported list (shown next to the export buttons and in the PDF summary)
    const activeExportFilters = [
        search.length >= 3 && search.trim() && [t("search"), search.trim()],
        statusFilter !== t("all") && [t("status"), statusFilter],
        channelFilter !== t("all") && [t("channel"), channelLabel(channelFilter)],
        columnFilters.holderName.trim() && [t("owner"), columnFilters.holderName.trim()],
        columnFilters.bookingReference.trim() && [t("booking"), columnFilters.bookingReference.trim()],
        columnFilters.number.trim() && [t("ticket"), columnFilters.number.trim()],
        columnFilters.destinationPort.trim() && [t("arrival_port_short"), columnFilters.destinationPort.trim()],
        columnFilters.type && [t("type"), TYPE_FILTER_OPTIONS.find(o => o.value === columnFilters.type)?.label],
        columnFilters.tariff.trim() && [t("tariff"), columnFilters.tariff.trim()],
        columnFilters.total.trim() && [t("total"), columnFilters.total.trim()],
        columnFilters.boarded.trim() && [t("boarded"), columnFilters.boarded.trim()],
        columnFilters.status && [t("status"), columnFilters.status],
        columnFilters.channel && [t("channel"), channelLabel(columnFilters.channel)],
    ].filter(Boolean);

    const isExportFiltered = activeExportFilters.length > 0;
    const exportFileName = `${description}_${departureTime}${isExportFiltered ? "_filtered" : ""}`;

    const handleCheckInOut = (ticket, status) => {
        const isBoarded = status === t("boarded");

        const confirmed = window.confirm(
            isBoarded
                ? t("confirm_checkout_ticket")
                : t("confirm_checkin_ticket")
        );

        if (!confirmed) return;

        navigate(
            `/pages/boardingTickets/checkin/${routeId}/${ticket.departurePort}/${ticket.number}`
        );
    };

    return (
        <Container fluid className="py-4">

            <div className="mb-4">
                <h3 className="fw-bold mb-1">{t('tickets')}</h3>

                <div className="text-muted">{description}</div>

                {departureTime && (
                    <small className="text-muted">
                        {t('departure_time')}: {new Date(departureTime).toLocaleString()}
                    </small>
                )}
            </div>

            <Row className="g-3 mb-4">
                <StatCard
                    title={t('total')}
                    color="secondary"
                    //grandTotal={stats.totalGrand}
                    pax={stats.passengers}
                    inf={stats.infants}
                    acc={stats.accessories}
                    vei={stats.vehicles}
                />

                <StatCard
                    title={t("boarded")}
                    color="success"
                    //grandTotal={stats.boardedGrand}
                    pax={stats.boardedPassengers}
                    inf={stats.boardedInfants}
                    acc={stats.boardedAccessories}
                    vei={stats.boardedVehicles}
                />

                <StatCard
                    title="No Show"
                    color="danger"
                    //grandTotal={stats.noShowGrand}
                    pax={stats.passengers - stats.boardedPassengers}
                    inf={stats.infants - stats.boardedInfants}
                    acc={stats.accessories - stats.boardedAccessories}
                    vei={stats.vehicles - stats.boardedVehicles}
                />
            </Row>

            <Card className="shadow-sm border-0">

                <Card.Body>

                    <Row className="g-3 mb-4">

                        <Col lg={6}>
                            <Form.Label>{t("search")}</Form.Label>
                            <Form.Control
                                placeholder={t("search_by_passenger_booking_ticket")}
                                value={search}
                                /*onChange={(e) => {
                                    const value = e.target.value;
                                    setSearch(value);
                                    handleSearch(value);
                                }}*/
                                onChange={(e) => setSearch(e.target.value)}

                            />
                        </Col>

                        <Col lg={3}>
                            <Form.Label>
                                {t("status")}
                            </Form.Label>
                            <Form.Select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                            >
                                <option>{t("all")}</option>
                                <option>{t('boarded')}</option>
                                <option>No Show</option>
                            </Form.Select>
                        </Col>
                        <Col lg={3}>
                            <Form.Label>
                                {t("channel")}
                            </Form.Label>

                            <Form.Select
                                value={channelFilter}
                                onChange={(e) => setChannelFilter(e.target.value)}
                            >
                                <option>{t("all")}</option>
                                <option value="ticketOffice">
                                    {t("ticket_office")}
                                </option>
                                <option value="online">
                                    {t("online")}
                                </option>
                            </Form.Select>
                        </Col>

                        <Row className="g-2 justify-content-end">

                            <Col xs="auto" className="d-flex align-items-center">
                                <span
                                    className={`${styles.exportScope} ${isExportFiltered ? styles.exportScopeFiltered : ""}`}
                                    title={activeExportFilters.map(([label, value]) => `${label}: ${value}`).join("\n")}
                                >
                                    <i className={`fa-solid ${isExportFiltered ? "fa-filter" : "fa-list"}`} aria-hidden="true"></i>
                                    {isExportFiltered
                                        ? t("export_scope_filtered", { rows: exportRows.length })
                                        : t("export_scope_all", { rows: exportRows.length })}
                                </span>
                            </Col>

                            <Col xs="auto">
                                <div className={styles.exportGroup} role="group" aria-label={t("export")}>
                                    <button
                                        type="button"
                                        className={`${styles.exportButton} ${styles.exportCsv}`}
                                        title={t("export_csv")}
                                        disabled={exportRows.length === 0}
                                        onClick={() =>
                                            exportTicketsCsv(
                                                exportRows,
                                                exportFileName
                                            )
                                        }
                                    >
                                        <i className="fa-solid fa-file-csv" aria-hidden="true"></i>
                                        <span>CSV</span>
                                    </button>
                                    <button
                                        type="button"
                                        className={`${styles.exportButton} ${styles.exportPdf}`}
                                        title={t("export_pdf")}
                                        disabled={exportRows.length === 0}
                                        onClick={() =>
                                            exportTicketsPdf({
                                                rows: exportRows,
                                                fileName: exportFileName,
                                                title: t("tickets"),
                                                description,
                                                departureTime: new Date(departureTime).toLocaleString(),
                                                filters: activeExportFilters,
                                            })
                                        }
                                    >
                                        <i className="fa-solid fa-file-pdf" aria-hidden="true"></i>
                                        <span>PDF</span>
                                    </button>
                                </div>
                            </Col>

                        </Row>

                    </Row>

                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" />
                        </div>
                    ) : filteredTickets.length === 0 && !hasColumnFilters ? (
                        <div className="text-center py-5 text-muted">
                            {t('no_tickets_found')}
                        </div>
                    ) : (

                        <>
                            {selectedTickets.length > 0 && (
                                <Card className="mb-3 border-primary">
                                    <Card.Body className="d-flex justify-content-between align-items-center">

                                        <span>
                                            {selectedTickets.length} {t("tickets_selected")}
                                        </span>

                                        <div>
                                            <Button
                                                variant="success"
                                                onClick={() =>

                                                    openBulkConfirmation("checkin")
                                                }
                                            >
                                                {t("checkin_selected")}
                                            </Button>

                                            <Button
                                                variant="danger"
                                                className="ms-2"
                                                onClick={() =>
                                                    openBulkConfirmation("checkout")
                                                }
                                            >
                                                {t("checkout_selected")}
                                            </Button>
                                        </div>

                                    </Card.Body>
                                </Card>
                            )}
                            <DualScrollTable>
                                <Table hover className="align-middle table-nowrap">


                                    <thead className="table-light">
                                        <tr>
                                            <th className="text-center align-middle" style={{ minWidth: "44px", width: "44px", paddingLeft: "8px", paddingRight: "8px" }}>
                                                <Form.Check
                                                    type="checkbox"
                                                    className="d-flex justify-content-center align-items-center m-0 p-0"
                                                    checked={
                                                        paginatedTickets.length > 0 &&
                                                        paginatedTickets.every(t =>
                                                            selectedTickets.some(
                                                                s => s.number === t.number
                                                            )
                                                        )
                                                    }
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setSelectedTickets(prev => [
                                                                ...prev,
                                                                ...paginatedTickets
                                                                    .filter(ticket =>
                                                                        !prev.some(
                                                                            s => s.number === ticket.number
                                                                        )
                                                                    )
                                                                    .map(ticket => ({
                                                                        number: ticket.number,
                                                                        departurePort: ticket.departurePort,
                                                                        holderName: ticket.holderName
                                                                    }))
                                                            ]);
                                                        } else {
                                                            setSelectedTickets(prev =>
                                                                prev.filter(
                                                                    s =>
                                                                        !paginatedTickets.some(
                                                                            t => t.number === s.number
                                                                        )
                                                                )
                                                            );
                                                        }
                                                    }}
                                                />
                                            </th>
                                            <th>{t('owner')}</th>
                                            <th>{t('booking')}</th>
                                            <th>{t('ticket')}</th>
                                            <th >{t('arrival_port_short')}</th>
                                            <th className="text-center">{t('type')}</th>
                                            <th className="text-center">{t('tariff')}</th>
                                            <th className="text-center">{t('total')}</th>
                                            <th className="text-center">{t('boarded')}</th>
                                            <th>{t('status')}</th>
                                            <th>{t('channel')}</th>
                                            <th className="text-center">{t('actions')}</th>
                                        </tr>

                                        {/* Column filters */}
                                        <tr className={styles.filterRow}>
                                            <th className="text-center">
                                                {hasColumnFilters && (
                                                    <button
                                                        type="button"
                                                        className={styles.clearFiltersButton}
                                                        onClick={clearColumnFilters}
                                                        title={t("clear_filters")}
                                                        aria-label={t("clear_filters")}
                                                    >
                                                        <i className="fa-solid fa-filter-circle-xmark" aria-hidden="true"></i>
                                                    </button>
                                                )}
                                            </th>
                                            {[
                                                { key: "holderName", label: t('owner') },
                                                { key: "bookingReference", label: t('booking') },
                                                { key: "number", label: t('ticket') },
                                                { key: "destinationPort", label: t('arrival_port_short') },
                                            ].map(({ key, label }) => (
                                                <th key={key}>
                                                    <Form.Control
                                                        type="search"
                                                        size="sm"
                                                        value={columnFilters[key]}
                                                        onChange={(e) => updateColumnFilter(key, e.target.value)}
                                                        placeholder={t("filter_placeholder")}
                                                        aria-label={`${t("filter_placeholder")} ${label}`}
                                                        className={`${styles.columnFilter} ${columnFilters[key] ? styles.columnFilterActive : ""}`}
                                                    />
                                                </th>
                                            ))}
                                            <th>
                                                <Form.Select
                                                    size="sm"
                                                    value={columnFilters.type}
                                                    onChange={(e) => updateColumnFilter("type", e.target.value)}
                                                    aria-label={`${t("filter_placeholder")} ${t('type')}`}
                                                    className={`${styles.columnFilter} ${styles.columnFilterNarrow} ${columnFilters.type ? styles.columnFilterActive : ""}`}
                                                >
                                                    <option value="">{t("all")}</option>
                                                    {TYPE_FILTER_OPTIONS.map(option => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            </th>
                                            <th>
                                                <Form.Control
                                                    type="search"
                                                    size="sm"
                                                    value={columnFilters.tariff}
                                                    onChange={(e) => updateColumnFilter("tariff", e.target.value)}
                                                    placeholder={t("filter_placeholder")}
                                                    aria-label={`${t("filter_placeholder")} ${t('tariff')}`}
                                                    className={`${styles.columnFilter} ${columnFilters.tariff ? styles.columnFilterActive : ""}`}
                                                />
                                            </th>
                                            {[
                                                { key: "total", label: t('total') },
                                                { key: "boarded", label: t('boarded') },
                                            ].map(({ key, label }) => (
                                                <th key={key}>
                                                    <Form.Control
                                                        type="search"
                                                        size="sm"
                                                        inputMode="numeric"
                                                        value={columnFilters[key]}
                                                        onChange={(e) => updateColumnFilter(key, e.target.value)}
                                                        placeholder="#"
                                                        aria-label={`${t("filter_placeholder")} ${label}`}
                                                        className={`${styles.columnFilter} ${styles.columnFilterNarrow} text-center ${columnFilters[key] ? styles.columnFilterActive : ""}`}
                                                    />
                                                </th>
                                            ))}
                                            <th>
                                                <Form.Select
                                                    size="sm"
                                                    value={columnFilters.status}
                                                    onChange={(e) => updateColumnFilter("status", e.target.value)}
                                                    aria-label={`${t("filter_placeholder")} ${t('status')}`}
                                                    className={`${styles.columnFilter} ${columnFilters.status ? styles.columnFilterActive : ""}`}
                                                >
                                                    <option value="">{t("all")}</option>
                                                    <option value={t('boarded')}>{t('boarded')}</option>
                                                    <option value="No Show">No Show</option>
                                                    <option value="Pending">Pending</option>
                                                </Form.Select>
                                            </th>
                                            <th>
                                                <Form.Select
                                                    size="sm"
                                                    value={columnFilters.channel}
                                                    onChange={(e) => updateColumnFilter("channel", e.target.value)}
                                                    aria-label={`${t("filter_placeholder")} ${t('channel')}`}
                                                    className={`${styles.columnFilter} ${columnFilters.channel ? styles.columnFilterActive : ""}`}
                                                >
                                                    <option value="">{t("all")}</option>
                                                    {channelFilterOptions.map(issueType => (
                                                        <option key={issueType} value={issueType}>
                                                            {issueType === "ticketOffice" ? t("ticket_office") : issueType}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            </th>
                                            <th></th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {paginatedTickets.length === 0 && (
                                            <tr>
                                                <td colSpan={12} className="text-center text-muted py-4">
                                                    <i className="fa-solid fa-filter me-2" aria-hidden="true"></i>
                                                    {t("no_matching_tickets")}
                                                </td>
                                            </tr>
                                        )}
                                        {paginatedTickets.map(ticket => {

                                            const status = getTicketStatus(ticket);

                                            return (
                                                <tr key={ticket.id}>

                                                    <td className="text-center align-middle" style={{ minWidth: "44px", width: "44px", paddingLeft: "8px", paddingRight: "8px" }} >
                                                        <Form.Check
                                                            type="checkbox"
                                                            className="d-flex justify-content-center align-items-center m-0 p-0"
                                                            checked={
                                                                selectedTickets.some(
                                                                    s => s.number === ticket.number
                                                                )
                                                            }
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    setSelectedTickets(prev => [
                                                                        ...prev,
                                                                        {
                                                                            number: ticket.number,
                                                                            departurePort: ticket.departurePort,
                                                                            holderName: ticket.holderName
                                                                        }
                                                                    ]);
                                                                } else {
                                                                    setSelectedTickets(prev =>
                                                                        prev.filter(
                                                                            s => s.number !== ticket.number
                                                                        )
                                                                    );
                                                                }
                                                            }}
                                                        />
                                                    </td>

                                                    <td>
                                                        <div className="fw-semibold">
                                                            {ticket?.holderName ?? '-'}
                                                        </div>
                                                    </td>

                                                    <td>
                                                        {ticket?.bookingReference ?? '-'}
                                                    </td>

                                                    <td className="fw-bold">
                                                        {ticket?.number ?? '-'}
                                                    </td>

                                                    <td>
                                                        {ticket?.destinationPort ?? '-'}
                                                    </td>

                                                    <td>
                                                        <div className="small text-center">

                                                            {ticket?.passengers && (
                                                                <div className="mb-1">
                                                                    <Badge bg="secondary">PAX</Badge>
                                                                </div>
                                                            )}

                                                            {ticket?.infants && (
                                                                <div className="mb-1">
                                                                    <Badge bg="danger">INF</Badge>
                                                                </div>
                                                            )}


                                                            {ticket?.accessories && (
                                                                <div className="mb-1">
                                                                    <Badge bg="info">ACC</Badge>
                                                                </div>
                                                            )}


                                                            {ticket?.vehicles && (
                                                                <div>
                                                                    <Badge bg="warning" text="dark">
                                                                        VEI
                                                                    </Badge>
                                                                </div>
                                                            )}




                                                        </div>
                                                    </td>
                                                    <td>
                                                        {ticket?.tariff}
                                                    </td>

                                                    <td className="text-center">
                                                        <div>


                                                            {ticket?.passengers && (
                                                                <div>{ticket?.passengers?.total}</div>

                                                            )}
                                                            {ticket?.infants && (
                                                                <div>{ticket?.infants?.total}</div>

                                                            )}

                                                            {ticket?.accessories && (
                                                                <div>{ticket?.accessories?.total}</div>

                                                            )}


                                                            {ticket?.vehicles && (
                                                                <div>{ticket?.vehicles?.total}</div>

                                                            )}
                                                        </div>
                                                    </td>

                                                    <td className="text-center">
                                                        <div>


                                                            {ticket?.passengers && (
                                                                <div>{ticket?.passengers?.boarded}</div>

                                                            )}

                                                              {ticket?.infants && (
                                                                <div>{ticket?.infants?.boarded}</div>

                                                            )}


                                                            {ticket?.accessories && (
                                                                <div>{ticket?.accessories?.boarded}</div>

                                                            )}


                                                            {ticket?.vehicles && (
                                                                <div>{ticket?.vehicles?.boarded}</div>

                                                            )}
                                                        </div>
                                                    </td>

                                                    <td>
                                                        <Badge
                                                            bg={getBadgeVariant(status)}
                                                            text={status === "Pending" ? "dark" : undefined}
                                                        >
                                                            {status}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        {ticket?.issueType ?
                                                            (ticket?.issueType === "ticketOffice"
                                                                ? t("ticket_office")
                                                                : ticket?.issueType)
                                                            : "-"}
                                                    </td>
                                                    <td>
                                                        <div className="d-flex flex-wrap justify-content-center gap-2">

                                                            <Button
                                                                size="sm"
                                                                variant="outline-secondary"
                                                                className="d-flex align-items-center gap-1"
                                                                onClick={() =>
                                                                    navigate(
                                                                        `/pages/boardingTickets/details/${routeId}/${ticket.departurePort}/${ticket.number}`
                                                                    )
                                                                }
                                                            >
                                                                <i className="bi bi-eye"></i>
                                                                {t("details")}
                                                            </Button>


                                                            <Button
                                                                size="sm"
                                                                variant={status === t("boarded") ? "danger" : "success"}
                                                                onClick={() => openConfirmation(ticket, status)}
                                                            >
                                                                <i className="bi bi-check-circle me-1"></i>

                                                                {status === t("boarded")
                                                                    ? t("checkout")
                                                                    : t("checkin")}
                                                            </Button>

                                                        </div>
                                                    </td>

                                                </tr>
                                            );
                                        })}
                                    </tbody>

                                </Table></DualScrollTable>


                            <TablePagination
                                page={page}
                                totalPages={totalPages}
                                totalResults={totalResults}
                                limit={limit}
                                onPageChange={setPage}
                                onLimitChange={(size) => {
                                    setLimit(size);
                                    setSelectedTickets([]);
                                }}
                            /></>



                    )}

                </Card.Body>

            </Card>

            <Modal
                show={confirmation.show}
                onHide={() =>
                    setConfirmation({
                        show: false,
                        ticket: null,
                        action: null,
                    })
                }
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {confirmation.action === "checkin"
                            ? t("confirm_checkin_title")
                            : t("confirm_checkout_title")}
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>

                    <Alert
                        variant={
                            confirmation.action === "checkin"
                                ? "info"
                                : "warning"
                        }
                    >
                        {confirmation.action === "checkin"
                            ? t("checkin_warning")
                            : t("checkout_warning")}
                    </Alert>

                    {confirmation.ticket && (
                        <>
                            <Table bordered size="sm">
                                <tbody>
                                    <tr>
                                        <th>{t("owner")}</th>
                                        <td>{confirmation.ticket.holderName}</td>
                                    </tr>

                                    <tr>
                                        <th>{t("booking")}</th>
                                        <td>{confirmation.ticket.bookingReference}</td>
                                    </tr>

                                    <tr>
                                        <th>{t("ticket")}</th>
                                        <td>{confirmation.ticket.number}</td>
                                    </tr>

                                    <tr>
                                        <th>{t("destination")}</th>
                                        <td>{confirmation.ticket.destinationPort}</td>
                                    </tr>

                                    <tr>
                                        <th>PAX</th>
                                        <td>{confirmation.ticket.passengers?.total ?? 0}</td>
                                    </tr>

                                     <tr>
                                        <th>INF</th>
                                        <td>{confirmation.ticket.infants?.total ?? 0}</td>
                                    </tr>

                                    <tr>
                                        <th>ACC</th>
                                        <td>{confirmation.ticket.accessories?.total ?? 0}</td>
                                    </tr>

                                    <tr>
                                        <th>VEI</th>
                                        <td>{confirmation.ticket.vehicles?.total ?? 0}</td>
                                    </tr>
                                </tbody>
                            </Table>
                        </>
                    )}

                </Modal.Body>

                <Modal.Footer>

                    <Button
                        variant="secondary"
                        onClick={() =>
                            setConfirmation({
                                show: false,
                                ticket: null,
                                action: null,
                            })
                        }
                    >
                        {t("cancel")}
                    </Button>

                    <Button
                        variant={
                            confirmation.action === "checkin"
                                ? "success"
                                : "danger"
                        }
                        onClick={confirmAction}
                    >
                        {confirmation.action === "checkin"
                            ? t("checkin")
                            : t("checkout")}
                    </Button>

                </Modal.Footer>
            </Modal>

            <Modal
                show={bulkConfirmation.show}
                centered
                onHide={() =>
                    setBulkConfirmation({
                        show: false,
                        action: null,
                    })
                }
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {bulkConfirmation.action === "checkin"
                            ? t("confirm_checkin_title")
                            : t("confirm_checkout_title")}
                    </Modal.Title>
                </Modal.Header>


                <Modal.Body>

                    <Alert
                        variant={
                            bulkConfirmation.action === "checkin"
                                ? "info"
                                : "warning"
                        }
                    >
                        {bulkConfirmation.action === "checkin"
                            ? t("checkin_warning")
                            : t("checkout_warning")}
                    </Alert>


                    <div className="text-center">

                        <h4 className="fw-bold">
                            {selectedTickets.length}
                        </h4>

                        <span>
                            {t("tickets_selected")}
                        </span>

                    </div>

                </Modal.Body>


                <Modal.Footer>

                    <Button
                        variant="secondary"
                        onClick={() =>
                            setBulkConfirmation({
                                show: false,
                                action: null
                            })
                        }
                    >
                        {t("cancel")}
                    </Button>


                    <Button
                        variant={
                            bulkConfirmation.action === "checkin"
                                ? "success"
                                : "danger"
                        }
                        onClick={confirmBulkAction}
                    >
                        {bulkConfirmation.action === "checkin"
                            ? t("checkin")
                            : t("checkout")}
                    </Button>

                </Modal.Footer>

            </Modal>

        </Container>
    );
};

export default BoardingTickets;